/* eslint-disable react/no-array-index-key */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-confusing-arrow */
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import InputContainer from './InputContainer';
import styles from './Inputs.module.scss';

export type AutoCompleteOption = { label: string };

interface IAutoCompleteInputProps {
    title: string;
    labelValue: string | undefined | null;
    options: AutoCompleteOption[];
    handleTextChange: (labelValue: string) => void;
    required?: boolean;
    errorMessage?: string;
    clearOnSelect?: boolean;
    track: TrackingEvent;
}

const escapeRegularExpression = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getSearchTokens = (value: string): string[] =>
    value
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter((token) => token.length > 0);

const optionMatchesTokens = (label: string, tokens: string[]): boolean => {
    if (!tokens.length) {
        return true;
    }
    const normalizedLabel = label.toLowerCase();
    return tokens.every((token) => normalizedLabel.includes(token));
};

const buildHighlightRegularExpression = (tokens: string[]): RegExp | null => {
    if (!tokens.length) {
        return null;
    }
    const escapedTokens = tokens.map(escapeRegularExpression);
    const pattern = escapedTokens.join('|');
    return new RegExp(`(${pattern})`, 'ig');
};

const AutoCompleteInput: React.FC<IAutoCompleteInputProps> = (props: IAutoCompleteInputProps) => {
    const [isValid, setIsValid] = useState<boolean>(true);
    const [inputValue, setInputValue] = useState<string>(props.labelValue ?? '');

    useEffect(() => {
        if (!props.clearOnSelect) {
            setInputValue(props.labelValue ?? '');
        }
    }, [props.labelValue, props.clearOnSelect]);

    const selectedValue: AutoCompleteOption | null = useMemo(() => {
        if (props.clearOnSelect) {
            return null;
        }
        return props.options.find((option) => option.label === props.labelValue) ?? null;
    }, [props.options, props.labelValue, props.clearOnSelect]);

    const searchTokens: string[] = useMemo(() => getSearchTokens(inputValue), [inputValue]);

    const filteredOptions: AutoCompleteOption[] = useMemo(
        () => props.options.filter((option) => optionMatchesTokens(option.label, searchTokens)),
        [props.options, searchTokens],
    );

    const highlightRegularExpression: RegExp | null = useMemo(() => buildHighlightRegularExpression(searchTokens), [searchTokens]);

    const handleBlur = (): void => {
        if (props.required) {
            setIsValid(!!props.labelValue);
        }
    };

    return (
        <>
            <InputContainer title={props.title}>
                <Autocomplete<AutoCompleteOption, false, false, false>
                    className={isValid ? styles.input_auto_complete : styles.input_auto_complete_error}
                    value={selectedValue}
                    inputValue={inputValue}
                    onInputChange={(_event, newInputValue, reason) => {
                        if (reason === 'input') {
                            setInputValue(newInputValue);
                            props.handleTextChange(newInputValue ?? '');
                            if (props.required && newInputValue) {
                                setIsValid(true);
                            }
                        }

                        if (reason === 'clear') {
                            setInputValue('');
                            props.handleTextChange('');
                            if (props.required) {
                                setIsValid(false);
                            }
                        }
                    }}
                    onChange={(_event, newValue, reason) => {
                        if (reason === 'selectOption' && newValue) {
                            props.handleTextChange(newValue.label);

                            if (props.clearOnSelect) {
                                setInputValue('');
                            } else {
                                setInputValue(newValue.label);
                            }

                            setIsValid(true);
                        }

                        if (reason === 'clear') {
                            props.handleTextChange('');
                            setInputValue('');
                            if (props.required) {
                                setIsValid(false);
                            }
                        }
                    }}
                    disablePortal
                    clearOnBlur={false}
                    options={filteredOptions}
                    filterOptions={(options) => options}
                    ListboxProps={{
                        style: {
                            maxHeight: 400,
                            overflow: 'auto',
                        },
                    }}
                    sx={{
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                            paddingRight: '64px',
                        },
                    }}
                    renderOption={(optionProps, option) => {
                        const label = option.label;

                        if (!highlightRegularExpression) {
                            return <li {...optionProps}>{label}</li>;
                        }

                        const labelParts = label.split(highlightRegularExpression);

                        return (
                            <li {...optionProps}>
                                <span
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        font: 'inherit',
                                        lineHeight: 'inherit',
                                    }}
                                >
                                    {labelParts.map((part, index) =>
                                        index % 2 === 1 ? (
                                            <span
                                                key={index}
                                                style={{
                                                    backgroundColor: styles.autocompleteHighlightColor,
                                                    font: 'inherit',
                                                    lineHeight: 'inherit',
                                                    padding: 0,
                                                    margin: 0,
                                                    display: 'inline',
                                                }}
                                            >
                                                {part}
                                            </span>
                                        ) : (
                                            <span
                                                key={index}
                                                style={{
                                                    font: 'inherit',
                                                    lineHeight: 'inherit',
                                                    padding: 0,
                                                    margin: 0,
                                                    display: 'inline',
                                                }}
                                            >
                                                {part}
                                            </span>
                                        ),
                                    )}
                                </span>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    onBlur={handleBlur}
                />
            </InputContainer>
            {!isValid && props.errorMessage && <p className={styles.input_error_message}>{props.errorMessage}</p>}
        </>
    );
};

export default AutoCompleteInput;
